import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Bell, Phone, User, Clock, X, Calendar, Bot, PhoneCall, ClipboardList } from 'lucide-react'
import { CallsService } from '../../services'
import type { CallbackOrigin, UpcomingCallbackRow } from '../../services/calls'
import { useAuth } from '../../contexts/AuthContext'
import { formatDate as formatDateIn, formatTime as formatTimeIn } from '../../utils/format'
import { Badge, type BadgeTone } from '../ui/Badge'

interface CallbackNotification {
  id: string
  contactName: string
  contactPhone: string
  scheduledTime: string
  campaignName?: string
  notes?: string
  isPending: boolean
  isOverdue: boolean
  urgencyLevel: 'overdue' | 'critical' | 'urgent' | 'soon' | 'scheduled'
  /** #468 — absent si le serveur ne la donne pas (rien n'est alors affiché). */
  origin?: CallbackOrigin
  requestedByName?: string | null
}

/**
 * #468 — la ligne dit d'où vient le rappel : posé en appel, posé par
 * l'assistant (et pour qui), ou simple fiche de campagne « Rappel prévu ».
 */
const ORIGIN_ICONS: Record<CallbackOrigin, typeof Bot> = {
  CALL: PhoneCall,
  ASSISTANT: Bot,
  CAMPAIGN_CONTACT: ClipboardList,
}

export function callbackOriginLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  origin: CallbackOrigin,
  requestedByName?: string | null,
): string {
  if (origin === 'ASSISTANT') {
    return requestedByName
      ? t('callbacks.origin.assistantFor', { name: requestedByName })
      : t('callbacks.origin.assistant')
  }
  return t(origin === 'CALL' ? 'callbacks.origin.call' : 'callbacks.origin.campaignContact')
}

/**
 * Niveau d'urgence → ton de badge (LOT-H8, §1.2). `danger` reste réservé à
 * l'échec : un rappel dont l'heure est PASSÉE en est un ; « critique » et
 * « urgent » sont encore rattrapables, donc des avertissements.
 */
const URGENCY_TONES: Record<CallbackNotification['urgencyLevel'], BadgeTone> = {
  overdue: 'danger',
  critical: 'warning',
  urgent: 'warning',
  soon: 'accent',
  scheduled: 'neutral',
}

interface CallbacksNotificationCenterProps {
  onCallbackClick?: (callback: CallbackNotification) => void
}

/** #382 — la cloche, à la hauteur des autres puces de la barre du haut (32 px). */
export const BELL_CHIP_CLASS =
  'group relative flex h-8 w-8 items-center justify-center rounded-pill border border-border-secondary bg-background-secondary ' +
  'text-foreground-secondary transition-colors hover:border-primary-500/50 hover:bg-primary-500/20 hover:text-primary-400 ' +
  "before:absolute before:left-1/2 before:top-1/2 before:h-touch before:w-touch before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']"

export const CallbacksNotificationCenter: React.FC<CallbacksNotificationCenterProps> = ({ 
  onCallbackClick 
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [notifiedCallbacks, setNotifiedCallbacks] = useState<Set<string>>(new Set())
  const [dismissedCallbacks, setDismissedCallbacks] = useState<Set<string>>(new Set())
  // ✅ FIX: Persist seen notifications in localStorage to survive refresh/logout
  const [seenCallbackIds, setSeenCallbackIds] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('seenCallbackIds')
      return saved ? new Set(JSON.parse(saved)) : new Set()
    } catch {
      return new Set()
    }
  })
  const dropdownRef = useRef<HTMLDivElement>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const { user } = useAuth()
  const { t } = useTranslation(['shell'])
  
  // ✅ Utiliser le fuseau horaire de l'utilisateur depuis les settings
  const userTimezone = (user as any)?.timezone || 'Europe/Paris'

  // Récupérer les callbacks programmés (optimisé avec endpoint dédié)
  const { data: callbacks = [], refetch } = useQuery<UpcomingCallbackRow[]>({
    queryKey: ['callbacks-pending'],
    queryFn: async () => {
      return await CallsService.getUpcomingCallbacks()
    },
    refetchInterval: 30000 // Refresh toutes les 30 secondes
  })

  // ✅ Calculer l'urgence en fonction du temps restant
  const getUrgencyLevel = (scheduledTime: Date, now: Date): 'overdue' | 'critical' | 'urgent' | 'soon' | 'scheduled' => {
    const diffMs = scheduledTime.getTime() - now.getTime()
    const diffMinutes = diffMs / (1000 * 60)
    
    if (diffMinutes < 0) return 'overdue'        // En retard
    if (diffMinutes <= 5) return 'critical'       // < 5 min = critique
    if (diffMinutes <= 15) return 'urgent'        // 5-15 min = urgent
    if (diffMinutes <= 60) return 'soon'          // 15-60 min = bientôt
    return 'scheduled'                            // > 1h = programmé
  }

  // Transformer les callbacks en notifications et filtrer les dismissées
  const notifications: CallbackNotification[] = callbacks
    .filter((call: any) => !dismissedCallbacks.has(call.id))
    .map((call: any) => {
      const scheduledTime = new Date(call.scheduledCallbackTime)
      const now = new Date()
      const urgencyLevel = getUrgencyLevel(scheduledTime, now)

      return {
        id: call.id,
        contactName: call.contact?.name || call.twilioFromNumber || call.twilioToNumber || t('callbacks.unknownContact'),
        contactPhone: call.contact?.phone || call.twilioFromNumber || call.twilioToNumber || '',
        scheduledTime: call.scheduledCallbackTime,
        campaignName: call.campaign?.name,
        notes: call.notes,
        isPending: urgencyLevel === 'critical' || urgencyLevel === 'urgent',
        isOverdue: urgencyLevel === 'overdue',
        urgencyLevel, // ✅ Ajout du niveau d'urgence
        origin: call.callbackOrigin,
        requestedByName: call.callbackRequestedBy?.name ?? null,
      }
    })
    // LOT-RAPPELS (RP-2) — le serveur sert désormais aussi les rappels ÉCHUS
    // (fenêtre [now−7j, now+24h]) : ils s'affichent dans le rendu « overdue »
    // existant, TRIÉS AVANT les à-venir. Le tri serveur (échéance asc) donne
    // déjà cet ordre ; il est refait ici pour que la promesse tienne même si
    // l'ordre amont change.
    .sort((a, b) => {
      if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1
      return new Date(a.scheduledTime).getTime() - new Date(b.scheduledTime).getTime()
    })

  // Callbacks qui nécessitent une attention (dans les 10 prochaines minutes ou en retard)
  const urgentCallbacks = notifications.filter(n => n.isPending || n.isOverdue)
  
  // ✅ Compter seulement les callbacks NON VUS pour le badge
  const unseenUrgentCount = urgentCallbacks.filter(n => !seenCallbackIds.has(n.id)).length
  const hasUnseenNotifications = notifications.some(n => !seenCallbackIds.has(n.id))

  // Jouer un son de notification pour les nouveaux callbacks urgents
  useEffect(() => {
    urgentCallbacks.forEach(callback => {
      if (!notifiedCallbacks.has(callback.id)) {
        // Notification browser
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(t('callbacks.notification.title'), {
            body: t('callbacks.notification.body', { name: callback.contactName }),
            icon: '/favicon.ico',
            tag: callback.id
          })
        }
        
        // Son de notification
        if (audioRef.current) {
          audioRef.current.play().catch(console.error)
        }
        
        setNotifiedCallbacks(prev => new Set(prev).add(callback.id))
      }
    })
  }, [urgentCallbacks, notifiedCallbacks])

  // Demander la permission pour les notifications
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  // ✅ FIX: Persist seenCallbackIds to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('seenCallbackIds', JSON.stringify([...seenCallbackIds]))
    } catch (error) {
      console.error('Failed to save seenCallbackIds to localStorage:', error)
    }
  }, [seenCallbackIds])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleCallbackClick = (callback: CallbackNotification) => {
    if (onCallbackClick) {
      onCallbackClick(callback)
    }
    setIsOpen(false)
  }

  const handleDismissCallback = (callbackId: string, event: React.MouseEvent) => {
    event.stopPropagation()
    setDismissedCallbacks(prev => new Set(prev).add(callbackId))
  }

  const handleDismissAll = () => {
    const allIds = notifications.map(n => n.id)
    setDismissedCallbacks(new Set(allIds))
  }

  return (
    <>
      {/* Audio pour les notifications */}
      <audio ref={audioRef} src="/notification.mp3" preload="auto" />
      
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        {/* Notification Button */}
        <button 
          onClick={() => {
            // ✅ Marquer tous les callbacks actuels comme "vus" au clic
            if (!isOpen) {
              const currentIds = notifications.map(n => n.id)
              setSeenCallbackIds(prev => new Set([...prev, ...currentIds]))
            }
            setIsOpen(!isOpen)
          }}
          // #382 (ÉCRANS-2) — même puce que l'horloge et la pastille des
          // manqués : 32 px dessinés (h-8 ; elle en faisait 34 : p-2 + bordure
          // + icône 16), pilule, surface et bordure du gabarit HEADER_CHIP ;
          // cible de 44 px par un pseudo-élément centré (procédé
          // d'`IconButton` `sm` — 6 px de débord, l'écart `gap-2` de la
          // rangée évite tout recouvrement avec la pastille voisine).
          className={BELL_CHIP_CLASS}
          title={t('callbacks.buttonTitle')}
          aria-label={t('callbacks.buttonTitle')}
        >
          <Bell size={16} className={`transition-all duration-300 ${unseenUrgentCount > 0 ? 'animate-bounce' : ''}`} />
          
          {/* ✅ Badge rouge seulement pour les callbacks urgents NON VUS */}
          {unseenUrgentCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-5 w-5 bg-gradient-to-br from-red-500 to-red-600 border-2 border-slate-900 items-center justify-center shadow-lg">
                <span className="text-[9px] font-bold text-white">{unseenUrgentCount}</span>
              </span>
            </span>
          )}
          
          {/* ✅ Petit point bleu pour notifications non vues non urgentes */}
          {hasUnseenNotifications && unseenUrgentCount === 0 && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 border-2 border-slate-900 rounded-full"></span>
          )}
        </button>

        {/* Dropdown Panel */}
        {isOpen && (
          <div className="absolute right-0 mt-3 w-96 bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-2xl border border-slate-600/40 rounded-2xl shadow-2xl z-[9999] animate-in fade-in slide-in-from-top-2 duration-200" style={{
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(59, 130, 246, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
          }}>
            {/* Header */}
            {/* CQ-005 — l'en-tête portait un dégradé bleu→violet (deux accents,
                §1.1) alors que le panneau est monté par le châssis : surface
                neutre surélevée, comme les en-têtes de tableaux (H5). */}
            <div className="px-5 py-4 border-b border-slate-700/30 bg-background-secondary/40">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-xl border border-blue-500/30">
                    <Calendar size={18} className="text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white">{t('callbacks.panelTitle')}</h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {t('callbacks.count', { count: notifications.length })}
                    </p>
                    {/* LOT-RAPPELS (RP-3) — l'horizon reste 24 h (choix le
                        moins invasif) mais il est désormais DIT, ainsi que la
                        survie 7 jours des rappels en retard (RP-2). */}
                    <p className="text-xs text-slate-500 mt-0.5">
                      {t('callbacks.windowHint')}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={handleDismissAll}
                  className="px-2.5 py-1 hover:bg-red-500/20 border border-red-500/30 rounded-lg transition-colors group"
                  title={t('callbacks.clearAll')}
                >
                  <span className="text-xs font-medium text-red-400 group-hover:text-red-300">{t('callbacks.clearAll')}</span>
                </button>
              </div>
            </div>

            {/* Callbacks List */}
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="px-5 py-8 text-center">
                  <Bell size={40} className="text-slate-600 mx-auto mb-3" />
                  <p className="text-sm text-slate-400">{t('callbacks.empty')}</p>
                </div>
              ) : (
                <div className="py-2">
                  {notifications.map((callback) => {
                    const scheduledDate = new Date(callback.scheduledTime)
                    const today = new Date()
                    const isToday = scheduledDate.toDateString() === today.toDateString()
                    
                    // ✅ Utiliser le fuseau horaire de l'utilisateur pour afficher les dates
                    // Le fuseau de l'utilisateur reste explicite : un rappel planifié
                    // se lit dans SON fuseau, pas dans celui de la machine.
                    const formatTime = (date: Date) => formatTimeIn(date, { timeZone: userTimezone })
                    const formatDate = (date: Date) => formatDateIn(date, { timeZone: userTimezone })
                    // #468 — origine inconnue (valeur hors liste) : rien d'affiché, jamais d'erreur.
                    const OriginIcon = callback.origin ? ORIGIN_ICONS[callback.origin] : undefined

                    return (
                      <div
                        key={callback.id}
                        className={`relative group w-full border-l-4 ${
                          callback.isOverdue 
                            ? 'border-red-500 bg-red-500/5' 
                            : callback.isPending 
                            ? 'border-yellow-500 bg-yellow-500/5'
                            : 'border-transparent'
                        }`}
                      >
                        <button
                          onClick={() => handleCallbackClick(callback)}
                          className="w-full px-5 py-3 hover:bg-slate-700/30 transition-colors text-left"
                        >
                          <div className="flex items-start gap-3">
                          {/* Avatar/Icon */}
                          <div className={`p-2 rounded-xl border ${
                            callback.isOverdue 
                              ? 'bg-red-500/20 border-red-500/40' 
                              : callback.isPending
                              ? 'bg-yellow-500/20 border-yellow-500/40'
                              : 'bg-blue-500/20 border-blue-500/40'
                          }`}>
                            <Phone size={16} className={
                              callback.isOverdue 
                                ? 'text-red-400' 
                                : callback.isPending
                                ? 'text-yellow-400'
                                : 'text-blue-400'
                            } />
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-semibold text-white truncate">
                                {callback.contactName}
                              </span>
                              {/* LOT-H8 — cinq niveaux d'urgence portaient cinq
                                  familles de couleur, dont un `orange` qui n'est
                                  même pas un token et un `yellow` hors du §1.2.
                                  Les cinq niveaux se rangent sur trois rôles :
                                  un rappel EN RETARD est un échec (`danger`),
                                  « critique » et « urgent » sont des
                                  avertissements, « bientôt » est l'accent, le
                                  reste est neutre. */}
                              <Badge tone={URGENCY_TONES[callback.urgencyLevel] ?? 'neutral'}>
                                {t(`callbacks.urgency.${callback.urgencyLevel}`)}
                              </Badge>
                            </div>
                            
                            <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                              <User size={12} />
                              <span>{callback.contactPhone}</span>
                            </div>

                            <div className="flex items-center gap-2 text-xs text-slate-400">
                              <Clock size={12} />
                              <span>
                                {t('callbacks.whenAt', {
                                  when: isToday ? t('callbacks.today') : formatDate(scheduledDate),
                                  time: formatTime(scheduledDate),
                                })}
                              </span>
                            </div>

                            {callback.origin && OriginIcon && (
                              <div
                                className="mt-1 flex items-center gap-2 text-xs text-slate-400"
                                data-callback-origin={callback.origin}
                              >
                                <OriginIcon size={12} aria-hidden="true" />
                                <span className="truncate">
                                  {callbackOriginLabel(t, callback.origin, callback.requestedByName)}
                                </span>
                              </div>
                            )}

                            {callback.campaignName && (
                              <div className="mt-1 text-xs text-blue-400">
                                📋 {callback.campaignName}
                              </div>
                            )}

                            {callback.notes && (
                              <div className="mt-1.5 text-xs text-slate-500 italic truncate">
                                "{callback.notes}"
                              </div>
                            )}
                          </div>
                        </div>
                        </button>
                        
                        {/* Bouton X qui apparaît au hover - style macOS */}
                        <button
                          onClick={(e) => handleDismissCallback(callback.id, e)}
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 hover:bg-slate-700/50 rounded-full"
                          title={t('callbacks.dismiss')}
                        >
                          <X size={14} className="text-slate-400 hover:text-red-400 transition-colors" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="px-5 py-3 border-t border-slate-700/30 bg-slate-800/30">
                <button 
                  onClick={(e) => {
                    e.stopPropagation() // ✅ Empêcher la fermeture du dropdown
                    refetch()
                    // ✅ FIX: Ne PAS fermer le dropdown pour voir les nouvelles notifications
                  }}
                  className="w-full py-2 px-4 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-xl text-sm font-semibold text-blue-400 transition-colors"
                >
                  {t('callbacks.refresh')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
